"use client";

import { useCallback, useMemo } from "react";
import { cn } from "@/lib/utils";
import {
  useAuthConnectState,
  type AuthConnectStatus,
} from "@/hooks/useAuthConnectState";
import { useIntegrations } from "@/hooks/useIntegrations";
import { makeNewAccountProbe } from "@/lib/connections-probe";
import { hintedAppLabel } from "@/lib/account-display";
import { IntegrationIcon } from "@/components/ui/IntegrationIcons";
import { useTaskStore } from "@/stores/taskStore";
import {
  resolveIntegrationDisplayName,
  getIntegrationColor,
  extractAuthToolkit,
} from "@/lib/composio-tools";
import { ToolSpinnerIcon } from "@/components/icons/ToolSpinnerIcon";
import type { ToolUseBlock } from "@/types";
import { ArrowRightIcon } from "@/components/icons/ArrowRightIcon";
import { CheckIcon } from "@/components/icons/CheckIcon";

// Composio Tool Router meta tools that handle auth flows
const COMPOSIO_AUTH_TOOLS = [
  "COMPOSIO_MANAGE_CONNECTIONS",
  "mcp__composio__COMPOSIO_MANAGE_CONNECTIONS",
];

// Patterns to detect auth required responses from Composio MCP
const AUTH_REQUIRED_PATTERNS = [
  /authentication\s+required/i,
  /not\s+connected/i,
  /connection\s+required/i,
  /please\s+authenticate/i,
  /auth_url/i,
  /authorization\s+required/i,
  /no\s+active\s+connection/i,
  /user\s+not\s+authenticated/i,
  /connect\.composio\.dev/i,
];

// Check if result indicates auth is required and extract auth URL if present
function parseAuthRequired(
  result: unknown,
  toolName?: string
): { isAuthRequired: boolean; authUrl?: string; toolkit?: string } {
  if (!result) return { isAuthRequired: false };

  const resultStr =
    typeof result === "string" ? result : JSON.stringify(result);

  // COMPOSIO_MANAGE_CONNECTIONS meta tool always contains auth info
  const isManageConnectionsTool =
    toolName &&
    COMPOSIO_AUTH_TOOLS.some((t) => toolName === t || toolName.endsWith(t));

  // Check for auth required patterns or meta tool
  const isAuthRequired =
    isManageConnectionsTool ||
    AUTH_REQUIRED_PATTERNS.some((pattern) => pattern.test(resultStr));

  if (!isAuthRequired) return { isAuthRequired: false };

  // Try to extract auth URL from result
  let authUrl: string | undefined;
  let toolkit: string | undefined;

  try {
    const resultObj =
      typeof result === "object" ? result : JSON.parse(resultStr);
    authUrl =
      resultObj?.auth_url ||
      resultObj?.authUrl ||
      resultObj?.redirect_url ||
      resultObj?.url ||
      resultObj?.connect_url ||
      resultObj?.connectUrl;
    toolkit = resultObj?.toolkit || resultObj?.app || resultObj?.appName;
  } catch {
    // Ignore parse errors — fall through to regex
  }

  // Try regex extraction for Connect Link URLs
  if (!authUrl) {
    const connectMatch = resultStr.match(
      /https?:\/\/connect\.composio\.dev\/[^\s"')}\]]+/i
    );
    if (connectMatch) {
      authUrl = connectMatch[0].replace(/[,;'"\\]+$/, "");
    }
  }

  // Fallback: any auth-like URL
  if (!authUrl) {
    const urlMatch = resultStr.match(
      /https?:\/\/[^\s"']+(?:auth|connect|oauth|login)[^\s"']*/i
    );
    if (urlMatch) {
      authUrl = urlMatch[0].replace(/[,;'"\\]+$/, "");
    }
  }

  // Try to extract toolkit name from URL path
  if (!toolkit && authUrl) {
    const toolkitMatch = authUrl.match(/\/(?:app|toolkit)\/([a-zA-Z0-9_-]+)/i);
    if (toolkitMatch) {
      toolkit = toolkitMatch[1].toLowerCase();
    }
  }

  // Fallback: extract toolkit from result string (handles nested/escaped Composio responses)
  if (!toolkit) {
    const toolkitStrMatch = resultStr.match(
      /\\?"toolkit\\?"\s*:\s*\\?"([a-zA-Z0-9_-]+)\\?"/i
    );
    if (toolkitStrMatch) {
      toolkit = toolkitStrMatch[1].toLowerCase();
    }
  }

  return { isAuthRequired: true, authUrl, toolkit };
}

/**
 * The pause card's face, lifted out of AuthCard so the composer's connect
 * panel wears exactly it (the founder, 2026-09-22: "the connection links etc
 * should appear just like how they appear when agent gets paused, same look,
 * same icons with a connect button"). Presentational: no store, no probe, no
 * verdicts. AuthCard's own DOM is byte for byte what it was before the lift,
 * because the two additions render nothing when their props are absent.
 */
export function ConnectFace({
  toolkit,
  brandColor,
  status,
  statusText,
  onConnect,
  onClose,
  flush,
  testId,
}: {
  toolkit: string;
  brandColor: string;
  status: AuthConnectStatus;
  /** The one line: the face never composes it, so a hint and add-mode read the same on both hosts. */
  statusText: string;
  onConnect: () => void;
  /** The composer panel's ×; the story never passes one. */
  onClose?: () => void;
  /** No outer margin: a deck spaces its own cards, the story keeps `my-3`. */
  flush?: boolean;
  testId?: string;
}) {
  const borderColor =
    status === "connected" ? "var(--status-success, #22c55e)" : brandColor;
  return (
    <div
      className={cn(
        !flush && "my-3",
        "rounded-8 border border-border-subtle bg-bg-subtle overflow-hidden"
      )}
      style={{ borderLeftWidth: 3, borderLeftColor: borderColor }}
      data-testid={testId}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <IntegrationIcon integration={toolkit} size={24} />
        <span
          className={cn(
            "text-3.5 flex-1 min-w-0",
            // The story's row is wide and keeps one line; the crown is 400 px
            // and cut "Connect Notion to con…" (the drill, 09-22), so a deck's
            // card wraps instead.
            flush ? "" : "truncate",
            status === "connected" ? "text-success" : "text-fg-default"
          )}
        >
          {statusText}
        </span>

        {status === "idle" && (
          <button
            onClick={onConnect}
            className="flex items-center gap-1.5 px-3 py-1.5 text-3.5 font-medium rounded-full transition-all bg-fg-default text-bg-base hover:opacity-90 active:scale-[0.97]"
          >
            Connect
            <ArrowRightIcon width={12} height={12} strokeWidth="2.5" />
          </button>
        )}

        {status === "connecting" && (
          <span className="flex items-center gap-1.5 px-3 py-1.5 text-3.5 font-medium rounded-full bg-fg-default/80 text-bg-base">
            <ToolSpinnerIcon width={12} height={12} className="text-bg-base" />
            Connecting…
          </span>
        )}

        {status === "connected" && (
          <span className="flex items-center gap-1.5 px-3 py-1.5 text-3.5 font-medium rounded-full bg-success/10 text-success border border-success/20">
            <CheckIcon width={12} height={12} strokeWidth="2.5" />
            Connected
          </span>
        )}

        {/* "Close panel", not "Close": the panel hosts draw this inside a card
            that has a Close of its own, so the two must read apart (AskShell
            says the same from its side). */}
        {onClose && (
          <button
            type="button"
            aria-label="Close panel"
            onClick={onClose}
            className="text-3.5 leading-none px-1 text-fg-faint hover:text-fg-default transition-colors"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}

/** Compact inline auth card — idle → connecting → connected (→ back to idle
 *  on an abandoned OAuth).
 *
 *  No more polling. The button state machine lives in useAuthConnectState
 *  (shared with ComposioAuthInput): probe /my-connections on mount so
 *  revisiting an old task whose toolkit is already connected renders
 *  "Connected" immediately, and probe again on tab return — a positive
 *  result flips to 'connected'; a negative result mid-flow (the user
 *  cancelled on the provider page — no callback ever fires for that) walks
 *  grace rechecks covering the /verify race, then resets to 'idle' so the
 *  Connect button comes back.
 *
 *  The late-OAuth case (user comes back hours later and clicks Connect after
 *  the original interaction expired) is handled server-side by the /verify
 *  endpoint, which pushes a structured [SYSTEM_RESUME status="late_resolve"]
 *  continuation directly to the agent. No FE poll required — the tab-return
 *  probe also catches it.
 */
export function AuthCard({
  block,
  onConnectIntegration,
}: {
  block: ToolUseBlock;
  onConnectIntegration?: (toolkitSlug: string, authUrl?: string) => void;
}) {
  const authInfo = parseAuthRequired(block.result, block.toolName);
  const toolkit =
    block.display?.integration ||
    extractAuthToolkit(block.toolInput, block.result) ||
    authInfo.toolkit ||
    "service";
  // The brand: our catalog first; the provided name only for a toolkit we do not know.
  const displayName = resolveIntegrationDisplayName(
    toolkit,
    block.display?.integrationName
  );
  const brandColor = block.display?.brandColor || getIntegrationColor(toolkit);

  // The live pending auth ask for THIS toolkit on the selected task, if any.
  // Multi-account: it carries accountHint / mode:'add' (PR 2 runner emits
  // them; absent = today's ask). Object identity is stable in the store, so
  // the default equality re-renders only when the ask itself changes.
  const livePendingAuth = useTaskStore((s) => {
    const tid = s.selectedTaskId;
    if (!tid) return null;
    const pi = s.tasks.get(tid)?.pendingInteraction;
    return pi &&
      pi.type === "auth" &&
      pi.toolkit?.toLowerCase() === toolkit.toLowerCase()
      ? pi
      : null;
  });
  const accountHint = livePendingAuth?.accountHint;
  const addMode = livePendingAuth?.mode === "add";

  // Shared connect-state machine (useAuthConnectState). Signal precedence,
  // most → least authoritative:
  //   1. probe saw the toolkit `status: 'active'` → 'connected'.
  //   2. user clicked Connect and no negative tab-return settled → 'connecting'.
  //   3. Default → 'idle' (Connect button visible, clickable) — including
  //      after an abandoned OAuth (tab return + grace rechecks all negative).
  //
  // NOTE: `livePendingAuth` is intentionally NOT consulted here — it feeds the
  // hint/mode copy and nothing else. We tried gating connected on
  // `userClicked && !livePendingAuth` to mean "OAuth
  // resolved" — the assumption was that AuthCards always render with a
  // pending interaction in the store. They don't: when a Composio meta-
  // tool result includes an auth URL, the FE renders the inline AuthCard
  // off the tool result itself, with no substrate row backing it. The
  // `userClicked && !livePendingAuth` branch then evaluated TRUE on the
  // first render after click and flashed "Connected" before OAuth had
  // even started. Now we trust only the probe; the substrate-backed flow
  // (PostToolUse Composio auth hook + resume orchestrator) finalizes the
  // agent state separately.
  //
  // NOTE 2: `livePendingAuth` alone does NOT mean 'connecting'. The auth card
  // appears with a live pending interaction the moment the agent emits
  // INTERACTION_REQUIRED — the user hasn't done anything yet. Mapping
  // livePendingAuth → 'connecting' would render an unclickable spinner span
  // on initial render, blocking the user from ever starting OAuth.
  //
  // Add-mode: "connected" must mean a NEW account appeared — the default
  // probe reads the ACTIVE sibling and would flash Connected on mount. The
  // probe holds a baseline, so its identity is load-bearing in BOTH
  // directions: stable across renders (an inline probe re-records the
  // baseline off the post-grant wire and never flips) and re-minted on a
  // toolkit swap (a baseline read from another toolkit shares no ids with
  // this one, so every id would read as new).
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `toolkit` is a re-mint KEY for a stateful factory, not a captured value
  const newAccountProbe = useMemo(() => makeNewAccountProbe(), [toolkit]);
  const {
    status: connState,
    markClicked,
    resetToIdle,
  } = useAuthConnectState(toolkit, addMode ? newAccountProbe : undefined);
  // Add-mode mints its own URL: the parent's onConnectIntegration chain mints
  // a PLAIN connect, which the server short-circuits ("Already connected",
  // no URL) while a sibling is ACTIVE — the popup would never open.
  const { connect: connectIntegration } = useIntegrations({ autoLoad: false });

  const handleConnect = useCallback(() => {
    markClicked();
    try {
      if (addMode) {
        void (async () => {
          try {
            const { authUrl } = await connectIntegration(toolkit, {
              mode: "add",
            });
            if (authUrl) {
              const popup = window.open(authUrl, "_blank");
              if (!popup) resetToIdle();
            } else {
              resetToIdle();
            }
          } catch {
            resetToIdle();
          }
        })();
        return;
      }
      if (onConnectIntegration) {
        onConnectIntegration(toolkit);
      } else if (authInfo.authUrl) {
        const popup = window.open(authInfo.authUrl, "_blank");
        // Popup blocker / no-op return → the OAuth window never opened, so
        // we shouldn't sit on a 'connecting' spinner forever. Reset.
        if (!popup) resetToIdle();
      }
    } catch {
      // Mirror ComposioAuthInput's handler: any throw resets so the user
      // can retry. Stuck-in-connecting is the worst possible UX here.
      resetToIdle();
    }
  }, [
    addMode,
    connectIntegration,
    toolkit,
    authInfo.authUrl,
    onConnectIntegration,
    markClicked,
    resetToIdle,
  ]);

  const statusText =
    connState === "connected"
      ? `${displayName} connected`
      : connState === "connecting"
        ? `Connecting ${displayName}…`
        : addMode
          ? `Connect another ${displayName} account to continue`
          : `Connect ${hintedAppLabel(accountHint, displayName)} to continue`;

  return (
    <ConnectFace
      toolkit={toolkit}
      brandColor={brandColor}
      status={connState}
      statusText={statusText}
      onConnect={handleConnect}
    />
  );
}
